import type { Lead } from '../../../shared/types';
import { locateLeads } from '../data/geocode';
import { getSyncStatus } from '../data/sync';
import { pointsMap } from './pointsMap';
import type { MapPoint } from './pointsMap';

/** A small map of today's stops, numbered in visiting order and joined by a line (D65). Lazy: Maps
 *  JS loads after the cards are on screen; offline, unplaced or failing, it simply stays hidden. */

export const stopMap = (): {
  el: HTMLElement;
  show: (leads: readonly Lead[]) => Promise<void>;
  dispose: () => void;
} => {
  const { el, draw, clear, hasMap } = pointsMap("Map of today's stops");
  let shownKey = '';
  let seq = 0;

  const show = async (leads: readonly Lead[]): Promise<void> => {
    const mine = ++seq;
    const key = leads.map((l) => `${l.id}|${l.location_version}|${l.location_status}|${l.lat}|${l.lng}`).join(',');
    // offline is what the sync engine found out (a request that failed), not navigator.onLine, which
    // Chrome on a Mac can report false while connected (D68)
    if (leads.length === 0 || getSyncStatus().state === 'offline') {
      el.hidden = true;
      return;
    }
    if (key === shownKey && hasMap()) {
      el.hidden = false; // the same stops, already drawn: show them again after a lost connection
      return;
    }
    try {
      const located = await locateLeads(leads);
      if (mine !== seq) return;
      const points = leads.flatMap((l, i): MapPoint[] => {
        const at = located.get(l.id);
        return at ? [{ position: { lat: at.lat, lng: at.lng }, label: String(i + 1) }] : [];
      });
      el.hidden = points.length === 0;
      if (points.length === 0) return;
      await draw(points);
      if (mine === seq) shownKey = key;
    } catch {
      el.hidden = true;
    }
  };

  return {
    el,
    show,
    dispose: () => {
      seq++;
      clear();
    },
  };
};
